<template>
  <div class="flex flex-row items-center justify-between relative">
    <div class="flex-grow sm:text-center text-left">
      <h1 class="text-2xl text-blue-200 inline-block sm:text-4xl relative">Syndles
        <span v-if="activeDate"
              class="absolute top-0 left-full ml-1 text-[10px] leading-none font-bold bg-gradient-to-r from-green-400 to-teal-400 text-gray-900 rounded-full px-2 py-1 whitespace-nowrap shadow-sm">
          archive
        </span>
      </h1>
    </div>
    <div class="flex items-center gap-2.5 sm:gap-4 text-xl sm:text-2xl text-blue-200 sm:absolute sm:right-0">

      <Dictionary :words="foundWords"/>
      <font-awesome-icon class="cursor-pointer hover:text-blue-400" :icon="['fas', 'bullhorn']"
                         @click="showWhatsNew = true"/>
      <font-awesome-icon class="cursor-pointer hover:text-blue-400" :icon="['fas', 'chart-simple']"
                         @click="showLeaderboard = !showLeaderboard"/>
      <font-awesome-icon class="cursor-pointer hover:text-blue-400" :icon="['fas', 'trophy']"
                         @click="showAchievements = !showAchievements"/>
      <font-awesome-icon class="cursor-pointer hover:text-blue-400" :icon="['fas', 'gear']"
                         @click="showSettings = !showSettings"/>
      <div v-if="!isAuthenticated && !isLoading" class="relative inline-block">
        <button class="text-base sm:text-xl cursor-pointer bg-gradient-to-r from-green-400 to-teal-400 text-gray-900" @click="login">
          Login
        </button>
        <span class="absolute -top-2 -right-2 text-[10px] leading-none font-bold bg-gray-900 text-green-400 ring-1 ring-green-400 rounded-full px-1.5 py-0.5 pointer-events-none">
          5&times; XP
        </span>
      </div>
    </div>
  </div>

  <Modal :is-visible="showLeaderboard" @update:isVisible="showLeaderboard = $event" :heading="isAuthenticated ? statsTitle : 'Stats'">
    <div v-if="isAuthenticated">
      <Stats @tab-change="statsTitle = $event"/>
    </div>
    <div v-else>
      <div class="flex flex-col items-center text-center">
        <img src="/radial-icon.png" alt="logo for syndles" class="w-24 mt-2 mb-3"/>
        <h2 class="text-2xl font-bold">Track your progress</h2>
        <p class="text-sm text-gray-400 mt-1.5 max-w-xs">
          Create a free account to unlock your stats and earn
          <span class="text-gradient font-extrabold">5&times; XP</span> on every puzzle.
        </p>

        <ul class="w-full max-w-xs space-y-3 text-left my-6">
          <li class="flex items-center gap-3">
            <span class="flex items-center justify-center w-9 h-9 rounded-full bg-green-400/15 text-green-400 shrink-0">
              <font-awesome-icon :icon="['fas', 'chart-line']"/>
            </span>
            <span class="text-sm text-gray-200">Level up as you bank XP</span>
          </li>
          <li class="flex items-center gap-3">
            <span class="flex items-center justify-center w-9 h-9 rounded-full bg-green-400/15 text-green-400 shrink-0">
              <font-awesome-icon :icon="['fas', 'fire']"/>
            </span>
            <span class="text-sm text-gray-200">Build a daily streak you won't want to break</span>
          </li>
          <li class="flex items-center gap-3">
            <span class="flex items-center justify-center w-9 h-9 rounded-full bg-green-400/15 text-green-400 shrink-0">
              <font-awesome-icon :icon="['fas', 'crown']"/>
            </span>
            <span class="text-sm text-gray-200">Climb the all-time leaderboard</span>
          </li>
        </ul>

        <button @click="loginWithRedirect()"
            class="w-full max-w-xs bg-gradient-to-r from-green-400 to-teal-400 hover:bg-green-500 text-xl text-gray-900 py-2.5">
          Create a free account
        </button>
        <a class="text-sm text-gray-400 underline hover:text-white hover:cursor-pointer mt-3" @click="loginWithRedirect()">
          Already registered? Log in
        </a>
      </div>
    </div>
  </Modal>
  <Modal :is-visible="showAchievements" @update:isVisible="showAchievements = $event" heading="Achievements">
    <Achievements/>
  </Modal>
  <Modal :is-visible="showSettings" @update:isVisible="showSettings = $event" heading="Settings">
    <Settings @saved="showSettings = false"/>
  </Modal>
  <Modal :is-visible="showInstallPwa" @update:isVisible="showInstallPwa = $event" heading="Install App">
    <InstallPwa/>
  </Modal>
  <Modal :is-visible="showWhatsNew" @update:isVisible="onWhatsNewVisibility($event)" heading="What's new">
    <WhatsNew/>
  </Modal>
</template>
<script setup>

import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import Modal from "./Modal.vue";
import Leaderboard from "./Leaderboard.vue";
import {computed, onMounted, ref, watch} from "vue";
import {useArchive} from "../composables/useArchive.js";
import Dictionary from "./Dictionary.vue";
import {useGrid} from "../composables/useGrid.js";
import Settings from "./Settings.vue";
import Achievements from "./Achievements.vue";
import InstallPwa from "./InstallPwa.vue";
import WhatsNew from "./WhatsNew.vue";
import {changelog} from "../data/changelog.js";

const showLeaderboard = ref(false);
const showAchievements = ref(false);
const showSettings = ref(false);
const showInstallPwa = ref(false);

// Starting an archive run (e.g. via "Play this day" inside the Stats modal) should drop
// the modal so the practice board is visible underneath.
const {activeDate} = useArchive();
watch(activeDate, (date) => {
  if (date) showLeaderboard.value = false;
});

// The newest changelog entry's id doubles as the announcement version: when it
// changes, the modal auto-pops once for everyone. Edit changelog.js, not here.
const ANNOUNCEMENT_VERSION = changelog[0].id;
const showWhatsNew = ref(false);
const statsTitle = ref('Daily');

onMounted(() => {
  // Brand-new visitors have no userId yet (it's created async after mount).
  // Nothing is "new" to them, so mark the announcement seen without popping.
  const isNewUser = !localStorage.getItem('userId');
  if (isNewUser) {
    localStorage.setItem('seenAnnouncement', ANNOUNCEMENT_VERSION);
    return;
  }
  if (localStorage.getItem('seenAnnouncement') !== ANNOUNCEMENT_VERSION) {
    showWhatsNew.value = true;
  }
});

function onWhatsNewVisibility(visible) {
  showWhatsNew.value = visible;
  if (!visible) {
    localStorage.setItem('seenAnnouncement', ANNOUNCEMENT_VERSION);
  }
}


const {foundWords} = useGrid();

import {useAuth0} from "@auth0/auth0-vue";
import Stats from "./Stats.vue";

const {loginWithRedirect, isAuthenticated, logout, user, isLoading} = useAuth0();

async function login() {
  await loginWithRedirect();
}

function canInstall() {
  return !window.matchMedia('(display-mode: standalone)').matches;
}
</script>


<style scoped>

</style>